import {
  BALANCE,
  demolishRefund,
  ringOf,
  zoneOfRing,
  wearLevel,
  wearPercent,
  type ModuleState,
  type StationState,
} from "@station-keeping/sim";
import { useState } from "react";
import { play } from "../../game/commands";
import { useGameUi } from "../../game/store";
import { formatClock, formatDuration, formatNumber, t } from "../../i18n";
import { cssColor, stageColor } from "../../render/palette";
import { failureLabel, moduleDescription, moduleName, stageLabel, statusLabel } from "../labels";
import { Icon, PixelText } from "../pixel";
import { DANGER_INK, Key, Led, Plate, SectionTitle, WearGauge } from "../primitives";
import { ModuleThumb } from "../thumbs";
import { StageLeds } from "./FailuresPanel";
import { MaintenanceActions } from "./MaintenanceActions";
import { LevelLeds, UpgradeSection } from "./UpgradeSection";

const STATUS_COLORS: Record<ModuleState["status"], string> = {
  running: "var(--color-good)",
  building: "var(--color-accent)",
  off: "var(--color-dim)",
  repairing: "var(--color-repair)",
  upgrading: "var(--color-accent)",
  stopped: "var(--color-stop)",
  unstaffed: "var(--color-warn)",
  unpowered: "var(--color-warn)",
};

const WEAR_COLORS = { ok: "var(--color-good)", worn: "var(--color-warn)", critical: "var(--color-alert)" } as const;

/**
 * Fiche d'un module : ce qui presse en haut (la panne et ses interventions), puis l'usure, puis la
 * description ; les actions qui défont (veille, démantèlement) à part, en bas.
 */
export function ModulePanel({ state, module }: { state: StationState; module: ModuleState }) {
  const select = useGameUi((ui) => ui.select);
  const [confirmDemolish, setConfirmDemolish] = useState(false);
  const failure = module.failure;
  const level = wearLevel(module.wear);
  const ring = ringOf(module.x, module.y);
  const isCore = module.type === "core";
  const building = module.buildLeft > 0;
  const escalation =
    failure && failure.stage < 3 && !module.job ? (BALANCE.failures.stageSeconds[failure.stage] ?? 0) - (state.tick - failure.since) : null;

  return (
    <div>
      <Plate title={moduleName(module.type)} onClose={() => select(null)}>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.9375rem]">
          <span className="flex items-center gap-2" style={{ color: STATUS_COLORS[module.status] }}>
            <Led on color={STATUS_COLORS[module.status]} blink={module.status === "stopped"} />
            {statusLabel(module.status)}
          </span>
          <span className="text-dim">
            {isCore ? t("zone.core") : t("module.place", { ring, zone: t(`zone.${zoneOfRing(ring)}.short`) })}
          </span>
          {module.status === "running" && module.eff < 10_000 ? (
            <span className="text-warn">{t("module.efficiency", { percent: Math.floor(module.eff / 100) })}</span>
          ) : null}
        </p>
        {!isCore ? (
          <p className="mt-1.5 flex items-center gap-2 text-[0.9375rem] text-muted">
            <LevelLeds level={module.level} />
            {t("module.level", { level: module.level, max: BALANCE.upgrades.maxLevel })}
          </p>
        ) : null}
      </Plate>

      <div className="flex justify-center py-3">
        <ModuleThumb type={module.type} level={module.level} size="auto" />
      </div>

      {building ? (
        <p className="flex items-center gap-2 text-[0.9375rem] text-muted">
          <Led on blink color="var(--color-accent)" />
          {t("status.building")}
          <span className="ml-auto text-ink">
            <PixelText text={formatClock(module.buildLeft)} label={formatDuration(module.buildLeft)} />
          </span>
        </p>
      ) : null}

      {failure ? (
        <section className="well px-3 py-3" aria-label={failureLabel(failure.kind)}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-base font-semibold" style={{ color: cssColor(stageColor(failure.stage)) }}>
              {failureLabel(failure.kind)}
            </span>
            <StageLeds stage={failure.stage} />
          </div>
          <p className="mt-0.5 text-[0.9375rem] text-ink">{stageLabel(failure.stage)}</p>
          {failure.stage >= 2 ? <p className="mt-1 text-[0.9375rem] text-muted">{t(`failure.effect.${failure.kind}`)}</p> : null}
          {escalation !== null && escalation > 0 ? (
            <p className="mt-2 flex items-center justify-between gap-2 text-[0.9375rem] text-warn">
              {t("failure.escalatesLabel")}
              <PixelText text={formatClock(escalation)} label={formatDuration(escalation)} />
            </p>
          ) : null}
          <p className="mt-1 text-[0.875rem] text-dim">{t("failure.debt")}</p>
        </section>
      ) : null}

      {!isCore ? (
        <div className="mt-3">
          <MaintenanceActions state={state} module={module} />
        </div>
      ) : null}

      {!isCore && !building ? (
        <>
          <SectionTitle>{t("wear.label")}</SectionTitle>
          <div className="flex items-center gap-3">
            <WearGauge wear={module.wear / BALANCE.wear.max} color={WEAR_COLORS[level]} level={level} label={t("wear.label")} />
            <span style={{ color: WEAR_COLORS[level] }}>
              <PixelText text={`${wearPercent(module.wear)}%`} label={`${wearPercent(module.wear)} %`} />
            </span>
            <span className="text-[0.9375rem]" style={{ color: WEAR_COLORS[level] }}>
              {t(`wear.${level}`)}
            </span>
          </div>
        </>
      ) : null}

      {!isCore && !building ? <UpgradeSection state={state} module={module} /> : null}

      <SectionTitle>{t("module.about")}</SectionTitle>
      <p className="text-[0.9375rem] text-muted">{moduleDescription(module.type)}</p>

      {!isCore && !building ? (
        <section className="dotted mt-6 bg-top pt-4" aria-label={t("module.controls")}>
          <div className="flex flex-wrap items-center gap-2">
            <Key tone="quiet" size="sm" onClick={() => play({ type: "TOGGLE", moduleId: module.id, on: !module.enabled })}>
              <Icon name="power" />
              {module.enabled ? t("action.toggleOff") : t("action.toggleOn")}
            </Key>
            {/* L'action qui défait, à part : en corail dès le premier appui, confirmée au second. */}
            {confirmDemolish ? (
              <span className="ml-auto flex flex-wrap gap-2">
                <Key tone="quiet" size="sm" onClick={() => setConfirmDemolish(false)}>
                  {t("action.cancel")}
                </Key>
                <Key
                  tone="danger"
                  size="sm"
                  onClick={() => {
                    if (play({ type: "DEMOLISH", moduleId: module.id })) select(null);
                    setConfirmDemolish(false);
                  }}
                >
                  <Icon name="trash" />
                  {t("action.demolishConfirm", { amount: formatNumber(demolishRefund(state, module)) })}
                </Key>
              </span>
            ) : (
              <Key tone="quiet" size="sm" className="ml-auto" style={DANGER_INK} onClick={() => setConfirmDemolish(true)}>
                <Icon name="trash" />
                {t("action.demolish")}
              </Key>
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}
